import React, { useState, useEffect, useRef } from "react";
import CustomTabBar, { TabItem } from "../common-features/CustomTabBar";
import { Box, Chip, Typography, useMediaQuery, useTheme, Switch, FormControlLabel, ButtonBase, ToggleButtonGroup, styled, ToggleButton, Skeleton } from "@mui/material";
import Summary from "./Summary";
import SanctionDetails from "./SanctionDetails";
import ApplicationDetails from "./ApplicationDetails";
import ProcessingFee from "./ProcessingFee";
import LoanDocuments from "./LoanDocuments";
import DisbursementRequest from "./DisbursementRequest";
import ConnectWithSales from "./ConnectWithSales";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../services/redux/store";
import { isApplicationCompleted } from "../../components/Form/StepperDyanmicForm/helper";
import { IconButton, OutlinedPrimaryButton, OutlinedSmallButton } from "../../components/ui/Button";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SummaryIcon from "../../assets/svgTabIcons/SummaryIcon";
import ApplicationDetailsIcon from "../../assets/svgTabIcons/ApplicationDetailsIcon";
import SanctionDetailsIcon from "../../assets/svgTabIcons/SanctionDetailsIcon";
import ProcessingFeeIcon from "../../assets/svgTabIcons/ProcessingFeeIcon";
import LoanDocumentsIcon from "../../assets/svgTabIcons/LoanDocumentsIcon";
import DisbursementRequestIcon from "../../assets/svgTabIcons/DisbursementRequestIcon";
import ConnectWithSalesIcon from "../../assets/svgTabIcons/ConnectWithSalesIcon";
import documentsServices from "../../services/api/service/documentService";
import { setStatusPopup } from "../../services/redux/slices/statusPopupSlice";
import { setPartnerStartedJourney } from "../../services/redux/slices/stepperDynamicFormSlice";
import ChatIcon from "../../assets/svgIcons/message-circle-2.svg";
import ConnectChatComponent from "../../components/ui/ConnectChatComponent";
import { chatStatusConfig, setChatStatusConfig } from "../../services/redux/slices/chatSlice";
import chatServices from "../../services/api/service/chatService";
import { TruncatedTextWithTooltip } from "../../components/ui/TruncatedTextWithTooltip";
import { useLocation, useNavigate } from "react-router-dom";
import { ROUTES } from "../../routes/routerConfig";
import applicationDetailsServices from "../../services/api/service/applicationDetails";

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  border: `1px solid #00B3B5`,
  borderRadius: 999,
  padding: 2,
  backgroundColor: theme.palette.background.paper,
  boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.04)",
  "& .MuiToggleButton-root": {
    border: 0,
    borderRadius: 999,
    color: theme.palette.text.secondary,
    backgroundColor: "transparent",
    minHeight: 32,
    padding: "4px 8px",
    fontSize: "12px",
    fontWeight: 600,
    textTransform: "none",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "rgba(0, 163, 141, 0.08)",
    },
    "&.Mui-selected": {
      backgroundColor: "#00B3B5",
      color: theme.palette.primary.contrastText,
      boxShadow: "0px 4px 12px rgba(0, 179, 181, 0.18)",
      "&:hover": {
        backgroundColor: "#00B3B5",
        color: theme.palette.primary.contrastText,
      },
    },
  },
}));

const DATA_VIEW_STORAGE_KEY = "summaryDataView";
const DEFAULT_DATA_VIEW = "processio_data_view";
const CUSTOMER_PORTAL_DATA_VIEW = "customer_portal";
const isValidDataView = (value: string | null) =>
  value === DEFAULT_DATA_VIEW || value === CUSTOMER_PORTAL_DATA_VIEW;

const ViewSummary: React.FC = () => {
  const theme = useTheme();
  const isXsOrSm = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const location = useLocation();
  const applicationNumber = useSelector((state: RootState) => state.stepper.trackingId);
  const dispatch = useDispatch();
  const { horizontalSteps, activeHorizontalIndex, fetchUserApplicationData } = useSelector((state: RootState) => state.stepper);
  const partnerStartedJourney = useSelector((state: RootState) => state.stepper.partnerStartedJourney);
  const [activeTab, setActiveTab] = useState<string>("summary");
  const [enableCustomerJourney, setEnableCustomerJourney] = useState<boolean>(false);
  const [courseName, setCourseName] = useState<string | undefined>(undefined);
  const [documentsHeaderActionsPortalTarget, setDocumentsHeaderActionsPortalTarget] =
    useState<HTMLDivElement | null>(null);
  const applicationStatus  = useSelector((state: RootState) => state?.tabs?.applicationStatus );
  const isSubmittedApplication = [
    "application submitted",
    "submitted",
  ].includes(String(applicationStatus || fetchUserApplicationData?.status || "").toLowerCase());
  const chatRef = useRef<HTMLDivElement>(null);
  const connectButtonRef = useRef<HTMLButtonElement>(null);
  const [isChatConnectOpen, setIsChatConnectOpen] = useState(false);
  const { chatAccess, viewAccess } = useSelector(chatStatusConfig);
  const path = location.pathname;
  const isDocsPath =
    path === ROUTES.Application.mandatoryDocuments.locationPath ||
    path === ROUTES.Application.bulkDocuments.locationPath ||
    path === ROUTES.Application.customerUploadDocuments.locationPath;
  const activeLayoutTab = path === ROUTES.Application.index ? 0 : isDocsPath ? 1 : 2;
  const [dataView, setDataView] = useState<string>(() => {
    const storedDataView = localStorage.getItem(DATA_VIEW_STORAGE_KEY);
    return isValidDataView(storedDataView)
      ? storedDataView || DEFAULT_DATA_VIEW
      : DEFAULT_DATA_VIEW;
  });

  const tabs: TabItem[] = [
    {
      id: "summary",
      label: "Summary",
      icon: "",
      iconType: "svg",
      iconContent: <SummaryIcon />,
    },
    {
      id: "appDetails",
      label: "Application Details",
      icon: "",
      iconType: "svg",
      iconContent: <ApplicationDetailsIcon />,
    },
    // {
    //   id: "connect",
    //   label: "Connect with customer",
    //   icon: "",
    //   iconType: "svg",
    //   iconContent: <ConnectWithSalesIcon />,
    // },
  ];

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    // You can add logic here to render different content based on the active tab
  };

  // useEffect(() => {
  //   if (
  //     fetchUserApplicationData &&
  //     Array.isArray(fetchUserApplicationData.horizontalSteps) &&
  //     fetchUserApplicationData.horizontalSteps.length > 0 &&
  //     Array.isArray(fetchUserApplicationData.horizontalSteps[0]?.verticalSteps)
  //   ) {
  //     const courseStep =
  //       fetchUserApplicationData.horizontalSteps[0].verticalSteps.find(
  //         (step: any) => step.verticalStepName === "Provide Course Details"
  //       )

  //     if (courseStep && Array.isArray(courseStep.fields)) {
  //       const courseField = courseStep.fields.filter(
  //         (field: any) => field.courseName
  //       ).find((field: any) => field.courseName.includes("-"))

  //       if (courseField && typeof courseField.courseName === "string") {
  //         // setCourseName(courseField.courseName.replace(/^\d+-/, ""));
  //         setCourseName(courseField.courseName);
  //       } else {
  //         setCourseName(undefined);
  //       }
  //     } else {
  //       setCourseName(undefined);
  //     }
  //   } else {
  //     setCourseName(undefined);
  //   }
  // }, [fetchUserApplicationData]);

  // let activeStep =
  //   fetchUserApplicationData?.length > 0
  //     ? fetchUserApplicationData.length
  //     : activeHorizontalIndex;

  const completed = isApplicationCompleted(
    horizontalSteps,
    fetchUserApplicationData
  );
  const submissionDocument = async (submissionTrackingId: string, status: boolean) => {
    await documentsServices.submissionDocument(submissionTrackingId, status).then((resp: any) => {
      dispatch(setPartnerStartedJourney(status));
      dispatch(setStatusPopup({
        status: "success",
        message: resp?.data?.message,
      }));
    }).catch((err: any) => {
      dispatch(setStatusPopup({
        status: "error",
        message: err?.response?.data?.message,
      }));
    });
  };

  const getChatFeatureStatus = async (appNum: string) => {
    try {
      const res = await chatServices.getChatFeatureStatus({
        applicationId: appNum
      });

      const responseData = res.data?.data;
      if (responseData) {
        dispatch(setChatStatusConfig({
          chatAccess: responseData.chatAccess,
          viewAccess: responseData.viewAccess,
          loading: false
        }));
      }
    } catch (error) {
    }
  };

  useEffect(() => {
    if (applicationNumber) {
      getChatFeatureStatus(applicationNumber);
    }
  }, [applicationNumber]);

  // chat window open/close functions
  const handleChatConnectOpen = () => {
    setIsChatConnectOpen(true);
  };
  const handleChatConnectClose = () => setIsChatConnectOpen(false);

  // close chat window on outside click
  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (
        isChatConnectOpen &&
        chatRef.current &&
        !chatRef.current.contains(event.target) &&
        !connectButtonRef.current?.contains(event.target)
      ) {
        handleChatConnectClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isChatConnectOpen]);

  // prevent body scroll when chat is open
  useEffect(() => {
    if (isChatConnectOpen) {
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
      document.documentElement.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
      document.documentElement.style.overflow = "unset";
    };
  }, [isChatConnectOpen]);

  const callUpdateShowCustomerDataFlagApi = async (showCustomerData: boolean, applicationNumber: string) => {
    try {
      if (applicationNumber) {
        const payload = {
          trackingId: applicationNumber,
          showCustomerData: showCustomerData
        };
        await applicationDetailsServices.updateShowCustomerDataFlag(payload);
      }
    } catch (error) {
      console.error("Error updating show customer data flag:", error);
    }
  }

  useEffect(() => {
    if (
      partnerStartedJourney &&
      applicationNumber &&
      dataView === CUSTOMER_PORTAL_DATA_VIEW
    ) {
      callUpdateShowCustomerDataFlagApi(true, applicationNumber);
      // else {
      //   callUpdateShowCustomerDataFlagApi(false, applicationNumber);
      // }
    }
  }, [partnerStartedJourney, dataView, applicationNumber]);

  useEffect(() => {
    if (partnerStartedJourney) {
      localStorage.setItem(DATA_VIEW_STORAGE_KEY, dataView);
    } else if (fetchUserApplicationData?.partnerStartedJourney === false) {
      localStorage.removeItem(DATA_VIEW_STORAGE_KEY);
      if (dataView !== DEFAULT_DATA_VIEW) {
        setDataView(DEFAULT_DATA_VIEW);
      }
    }
  }, [partnerStartedJourney, dataView, fetchUserApplicationData]);

  return (
    <div
      className="existing-application"
      style={{ minHeight: "auto !important" }}
    >
      {!isXsOrSm && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1,
            flexShrink: 1,
            width: "100%",
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ minWidth: 0, display: "flex", alignItems: "center", gap: '5px' }}>
                <TruncatedTextWithTooltip
                  style={{ fontSize: "20px !important", fontWeight: 600 , color: '#008688' }}
                  text={courseName || "-"}
                  limit={15}
                />
                <Typography
                  style={{ fontSize: "20px !important", fontWeight: 600 , color: '#008688' }}
                >
                  [#{applicationNumber}]
                </Typography>
              </Box>
              <Chip
                label={applicationStatus}
                className="card-contaner-status"
              />
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box>
                {partnerStartedJourney && (
                  <StyledToggleButtonGroup
                    value={dataView}
                    exclusive
                    onChange={(e, newValue) => {
                      if (newValue) {
                        localStorage.setItem(DATA_VIEW_STORAGE_KEY, newValue);
                        setDataView(newValue);
                      }
                    }}
                  >
                    <ToggleButton
                      value={CUSTOMER_PORTAL_DATA_VIEW} aria-label="Customer Portal"
                    >
                      Customer Portal
                    </ToggleButton>
                    <ToggleButton
                      value={DEFAULT_DATA_VIEW} aria-label="Processio Data View">
                      Processio Data View
                    </ToggleButton>
                  </StyledToggleButtonGroup>
                )}
              </Box>
              {!partnerStartedJourney && !isSubmittedApplication && (
                <Box
                  ref={setDocumentsHeaderActionsPortalTarget}
                  sx={{
                    minWidth: 190,
                    minHeight: 39,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                  }}
                />
              )}
              <OutlinedPrimaryButton onClick={() => {
                window.history.back();
              }}><ArrowBackIcon width={18} height={18} /> Back</OutlinedPrimaryButton>
            </Box>
          </Box>

          {/* <FormControlLabel
            control={
              <Switch
                checked={enableCustomerJourney}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setEnableCustomerJourney(e.target.checked);
                  submissionDocument(applicationNumber, e.target.checked);
                }}
                sx={{
                  "& .MuiSwitch-switchBase": {
                    color: "rgba(0, 179, 181, 1)",
                    "&.Mui-checked": {
                      color: "rgba(0, 179, 181, 1)",
                      "& + .MuiSwitch-track": {
                        backgroundColor: "rgba(0, 179, 181, 1)",
                      },
                    },
                  },
                  "& .MuiSwitch-track": {
                    backgroundColor: "#E5E7EB",
                  },
                }}
              />
            }
            label="Enable Customer Journey"
            sx={{
              "& .MuiFormControlLabel-label": {
                fontSize: "14px",
                color: "#9CA3AF",
              },
            }}
          /> */}

          {/* 
          <IconButton
            sx={{
              height: "32px",
              mt: 2,
            }}
            onClick={handleSubmitForReview}
          >
            Submit For Review
          </IconButton>
           */}
        </Box>
      )}

      {/* <CustomTabBar
        tabs={tabs}
        activeTabId={activeTab}
        onTabChange={handleTabChange}
      /> */}

      <Box mt={1}>
        {activeTab === "summary" && (
          <Summary
            dataView={dataView}
            callUpdateShowCustomerDataFlagApi={callUpdateShowCustomerDataFlagApi}
            setEnableCustomerJourney={setEnableCustomerJourney}
            enableCustomerJourney={enableCustomerJourney}
            courseName={courseName}
            setCourseName={setCourseName}
            documentsHeaderActionsPortalTarget={documentsHeaderActionsPortalTarget}
          />
        )}
        {/* {activeTab === "sanctionDetails" && <SanctionDetails />} */}
        {/* {activeTab === "appDetails" && <ApplicationDetails />} */}
        {/* {activeTab === "processingFee" && <ProcessingFee />}
        {activeTab === "loanDocuments" && <LoanDocuments />}
        {activeTab === "disbursementRequest" && <DisbursementRequest />}
        {activeTab === "connect" && <ConnectWithSales />} */}
      </Box>

      {/* Chat Component */}
      {isChatConnectOpen && (
        <>
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.3)",
              zIndex: 1000,
              width: "100%",
              height: "100%",
            }}
            onClick={handleChatConnectClose}
          />

          <Box
            ref={chatRef}
            sx={{
              zIndex: 1301,
              position: "fixed",
              display: "flex",
              justifyContent: { xs: "center", md: "flex-end" },
              alignItems: "flex-end",
              left: { xs: 0, md: "auto" },
              right: { xs: 0, md: 32 },
              width: { xs: "100%", md: "auto" },
              maxWidth: { xs: "100%", md: "400px" },
              bottom: {
                xs: 0,
                md: 120,
              },
            }}
          >
            <ConnectChatComponent
              open={isChatConnectOpen}
              onClose={handleChatConnectClose}
            />
          </Box>
        </>
      )}

      {/* connect with customer floating button */}
      {viewAccess && (
        <Box
          ref={connectButtonRef}
          sx={{
            position: "fixed",
            bottom: { xs: 16, md: 32 },
            right: { xs: 16, md: 32 },
            zIndex: 1300,
          }}
        >
          <ButtonBase
            // onClick={() => handleTabChange("connect")}
            onClick={handleChatConnectOpen}
            sx={{
              borderRadius: "50%",
              position: "relative",
              width: 96,
              height: 96,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "transform 0.2s ease-in-out",
            }}
          >
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                backgroundColor: "#fecd3d",
                boxShadow: 3,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 2,
              }}
            >
              <Box
                component="img"
                src={ChatIcon}
                alt="Chat"
                sx={{
                  width: "80%",
                  height: "80%",
                  borderRadius: "50%",
                  objectFit: "cover",
                }}
              />
            </Box>

            <Box
              component="svg"
              viewBox="0 0 100 100"
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                pointerEvents: "none",
                zIndex: 1,
                overflow: "visible",
              }}
            >
              <path id="textCurve" d="M 8,50 A 42,42 0 0,0 92,50" fill="none" />

              <text
                fill="#333"
                fontSize="10"
                fontWeight="700"
                letterSpacing="0px"
                textAnchor="middle"
              >
                {/* startOffset="50%" keeps it centered at the bottom */}
                <textPath href="#textCurve" startOffset="50%">
                  Connect with customer
                </textPath>
              </text>
            </Box>
          </ButtonBase>
        </Box>
      )}
    </div>
  );
};

export default ViewSummary;
